/**
 * M-035: User Management UI
 *
 * Admin-only screen for managing depositary user accounts.
 * CRUD on users, role assignment, fund ownership, deactivation.
 * Uses M-004 User Management API.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/useAuth'
import { apiFetch, ApiError } from '../lib/api'
import {
  UserPlus,
  Users,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Save,
  X,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface User {
  id: string
  username: string
  display_name: string
  role: string
  is_active: boolean
  created_at: string
  updated_at: string
}

interface FundAssignment {
  fund_id: string
  fund_name: string
  role_tag: string
}

interface UserDetail extends User {
  fund_assignments: FundAssignment[]
}

// Lightweight subset of GET /api/funds used by the assignment picker.
interface FundOption {
  id: string
  name: string
  slug: string
}

type Role = 'ops_officer' | 'manager' | 'admin'

const ROLES: { value: Role; label: string }[] = [
  { value: 'ops_officer', label: 'Ops Officer' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
]

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusDot({ active }: { active: boolean }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{ backgroundColor: active ? 'var(--brand-teal)' : 'var(--brand-uncovered)' }}
      title={active ? 'Active' : 'Inactive'}
    />
  )
}

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    admin: 'var(--brand-blue)',
    manager: 'var(--brand-cyan)',
    ops_officer: 'var(--brand-teal)',
  }
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full text-white"
      style={{ backgroundColor: colors[role] ?? 'var(--brand-uncovered)' }}
    >
      {role.replace(/_/g, ' ')}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Create User Form
// ---------------------------------------------------------------------------

function CreateUserForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    username: '',
    password: '',
    display_name: '',
    role: 'ops_officer' as Role,
  })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async () => {
    setError(null)
    if (!form.username.trim() || !form.password || !form.display_name.trim()) {
      setError('All fields are required')
      return
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    setSaving(true)
    try {
      await apiFetch('/api/users', {
        method: 'POST',
        body: JSON.stringify(form),
      })
      onCreated()
    } catch (e) {
      if (e instanceof ApiError) {
        const detail = (e.body as Record<string, string>)?.detail
        setError(detail ?? `Error ${e.status}`)
      } else {
        setError('Failed to create user')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg p-5 bg-slate-50 space-y-4">
      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
        <UserPlus size={16} /> New user
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <input
          placeholder="Username"
          value={form.username}
          onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
          className="input-field"
          data-testid="user-form-username"
        />
        <input
          placeholder="Display name"
          value={form.display_name}
          onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
          className="input-field"
          data-testid="user-form-display-name"
        />
        <input
          type="password"
          placeholder="Password (min 8 chars)"
          value={form.password}
          onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          className="input-field"
          data-testid="user-form-password"
        />
        <select
          value={form.role}
          onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as Role }))}
          className="input-field"
          data-testid="user-form-role"
        >
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      {error && (
        <div className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle size={12} /> {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="btn-primary text-sm"
          data-testid="user-form-submit"
        >
          {saving ? 'Creating...' : 'Create'}
        </button>
        <button onClick={onCancel} className="btn-secondary text-sm">
          Cancel
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// User Detail Panel
// ---------------------------------------------------------------------------

function UserDetailPanel({
  userId,
  currentUserId,
  onUpdate,
}: {
  userId: string
  currentUserId: string
  onUpdate: () => void
}) {
  const [detail, setDetail] = useState<UserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [editRole, setEditRole] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // F-56: in-panel "Add fund assignment" form
  const [showAddFund, setShowAddFund] = useState(false)
  const [fundOptions, setFundOptions] = useState<FundOption[]>([])
  const [addFundForm, setAddFundForm] = useState<{ fund_id: string; role_tag: string }>({
    fund_id: '',
    role_tag: 'ops_officer',
  })
  const [addingFund, setAddingFund] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiFetch<UserDetail>(`/api/users/${userId}`)
      setDetail(data)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    load()
  }, [load])

  // Lazy-load funds the first time the admin opens the picker.
  useEffect(() => {
    if (!showAddFund || fundOptions.length > 0) return
    apiFetch<FundOption[]>('/api/funds')
      .then((data) => {
        setFundOptions(data)
        if (data.length > 0) setAddFundForm((p) => ({ ...p, fund_id: data[0].id }))
      })
      .catch(() => setError('Failed to load funds'))
  }, [showAddFund, fundOptions.length])

  const isSelf = userId === currentUserId

  const handleRoleChange = async (newRole: string) => {
    setError(null)
    try {
      await apiFetch(`/api/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      })
      setEditRole(null)
      load()
      onUpdate()
    } catch (e) {
      if (e instanceof ApiError) {
        setError((e.body as Record<string, string>)?.detail ?? 'Update failed')
      }
    }
  }

  const handleToggleActive = async () => {
    if (!detail) return
    setError(null)
    if (isSelf && detail.is_active) {
      setError('Cannot deactivate your own account')
      return
    }
    try {
      await apiFetch(`/api/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !detail.is_active }),
      })
      load()
      onUpdate()
    } catch (e) {
      if (e instanceof ApiError) {
        setError((e.body as Record<string, string>)?.detail ?? 'Update failed')
      }
    }
  }

  const handleRemoveFund = async (fundId: string) => {
    try {
      await apiFetch(`/api/users/${userId}/funds/${fundId}`, { method: 'DELETE' })
      load()
    } catch {
      // ignore
    }
  }

  // F-56: admin assigns a fund to this user with a given role_tag.
  const handleAddFund = async () => {
    if (!addFundForm.fund_id) return
    setError(null)
    setAddingFund(true)
    try {
      await apiFetch(`/api/users/${userId}/funds`, {
        method: 'POST',
        body: JSON.stringify(addFundForm),
      })
      setShowAddFund(false)
      setAddFundForm({ fund_id: '', role_tag: 'ops_officer' })
      load()
    } catch (e) {
      if (e instanceof ApiError) {
        setError((e.body as Record<string, string>)?.detail ?? `Error ${e.status}`)
      } else {
        setError('Failed to assign fund')
      }
    } finally {
      setAddingFund(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="w-4 h-4 border-2 border-nolos-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!detail) return null

  return (
    <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 space-y-3">
      {error && (
        <div className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      {/* Role */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500 w-16">Role</span>
        {editRole !== null ? (
          <div className="flex items-center gap-1">
            <select
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
              className="input-field text-sm py-0.5"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <button onClick={() => handleRoleChange(editRole)} className="text-nolos-primary">
              <Save size={14} />
            </button>
            <button onClick={() => setEditRole(null)} className="text-slate-400">
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditRole(detail.role)}
            className="text-sm text-slate-700 hover:text-nolos-primary"
          >
            <RoleBadge role={detail.role} />
          </button>
        )}
      </div>

      {/* Status toggle */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500 w-16">Status</span>
        <button
          onClick={handleToggleActive}
          className={`text-sm px-2 py-0.5 rounded ${
            detail.is_active
              ? 'bg-green-50 text-green-700 hover:bg-green-100'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
          data-testid={`deactivate-user-${userId}`}
        >
          {detail.is_active ? 'Active — click to deactivate' : 'Inactive — click to reactivate'}
        </button>
      </div>

      {/* Fund assignments */}
      <div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Fund assignments</span>
          {!showAddFund && (
            <button
              onClick={() => setShowAddFund(true)}
              className="text-xs text-nolos-primary hover:underline flex items-center gap-1"
              data-testid={`add-fund-assignment-${userId}`}
            >
              <Plus size={12} /> Assign fund
            </button>
          )}
        </div>
        {detail.fund_assignments.length === 0 ? (
          <p className="text-xs text-slate-400 mt-1">No fund assignments</p>
        ) : (
          <div className="mt-1 space-y-1">
            {detail.fund_assignments.map((fa) => (
              <div
                key={fa.fund_id}
                className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border border-slate-200"
              >
                <span className="text-slate-700 font-medium">{fa.fund_name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">{fa.role_tag.replace(/_/g, ' ')}</span>
                  <button
                    onClick={() => handleRemoveFund(fa.fund_id)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* F-56: add-assignment inline form */}
        {showAddFund && (
          <div className="mt-2 bg-white rounded px-3 py-2 border border-slate-200 space-y-2">
            <div className="flex items-center gap-2">
              <select
                value={addFundForm.fund_id}
                onChange={(e) => setAddFundForm((p) => ({ ...p, fund_id: e.target.value }))}
                className="input-field text-xs py-0.5 flex-1"
                data-testid={`add-fund-select-${userId}`}
                disabled={fundOptions.length === 0}
              >
                {fundOptions.length === 0 ? (
                  <option value="">Loading funds…</option>
                ) : (
                  fundOptions
                    .filter((f) => !detail.fund_assignments.some((a) => a.fund_id === f.id))
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))
                )}
              </select>
              <select
                value={addFundForm.role_tag}
                onChange={(e) => setAddFundForm((p) => ({ ...p, role_tag: e.target.value }))}
                className="input-field text-xs py-0.5"
                data-testid={`add-fund-role-${userId}`}
              >
                <option value="ops_officer">Ops officer</option>
                <option value="manager">Manager</option>
              </select>
              <button
                onClick={handleAddFund}
                disabled={addingFund || !addFundForm.fund_id}
                className="btn-primary text-xs px-2 py-0.5 disabled:opacity-50"
                data-testid={`add-fund-submit-${userId}`}
              >
                {addingFund ? '…' : 'Assign'}
              </button>
              <button
                onClick={() => {
                  setShowAddFund(false)
                  setError(null)
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function UsersPage() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<User[]>('/api/users')
      setUsers(data)
    } catch {
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div
        className="bg-white p-6"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        <p className="text-sm text-red-600">Access denied</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div
        className="bg-white p-6 flex items-center justify-between"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users size={20} /> User Management
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage user accounts, roles, and fund assignments.
          </p>
        </div>
        {!showCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary text-sm flex items-center gap-1"
            data-testid="create-user-button"
          >
            <UserPlus size={14} /> New user
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreate && (
        <CreateUserForm
          onCreated={() => {
            setShowCreate(false)
            load()
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* User list */}
      <div
        className="bg-white overflow-hidden"
        style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-nolos-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-8 gap-2">
            <p className="text-sm text-red-500">{error}</p>
            <button
              onClick={load}
              className="flex items-center gap-1 text-xs text-nolos-primary hover:underline"
            >
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p className="text-sm">No users found.</p>
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div className="grid grid-cols-[2rem_1fr_1fr_8rem_4rem_4rem] gap-2 px-4 py-2 bg-slate-50 text-xs text-slate-500 font-medium border-b border-slate-200">
              <div />
              <div>Username</div>
              <div>Display name</div>
              <div>Role</div>
              <div className="text-center">Status</div>
              <div className="text-center">Funds</div>
            </div>

            {/* Rows */}
            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`}>
                <button
                  onClick={() => setExpandedId(expandedId === u.id ? null : u.id)}
                  className="w-full grid grid-cols-[2rem_1fr_1fr_8rem_4rem_4rem] gap-2 px-4 py-3 text-sm text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
                  data-testid={`user-row-expand-${u.id}`}
                >
                  <div className="flex items-center">
                    {expandedId === u.id ? (
                      <ChevronDown size={14} className="text-slate-400" />
                    ) : (
                      <ChevronRight size={14} className="text-slate-400" />
                    )}
                  </div>
                  <div
                    className="text-slate-800 font-medium"
                    style={{ fontFamily: 'var(--font-mono)' }}
                  >
                    {u.username}
                  </div>
                  <div className="text-slate-600">{u.display_name}</div>
                  <div data-testid={`user-role-badge-${u.id}`}>
                    <RoleBadge role={u.role} />
                  </div>
                  <div
                    className="flex items-center justify-center"
                    data-testid={`user-status-${u.id}`}
                  >
                    <StatusDot active={u.is_active} />
                  </div>
                  <div className="text-center text-slate-500">--</div>
                </button>

                {expandedId === u.id && (
                  <UserDetailPanel
                    userId={u.id}
                    currentUserId={currentUser.user_id ?? currentUser.sub}
                    onUpdate={load}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
